/* eslint-disable react-refresh/only-export-components --
   Context module: CommandBarProvider, useCommandBarContext, and useRegisterCommands
   all share the CommandBarContext defined in this file. Splitting the hooks out for
   a dev-HMR benefit fragments the provider from its own accessors. */
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type CommandItem = {
  id: string;
  label: string;
  category: string;
  keywords?: string[];
  path?: string;
  action?: () => void | Promise<void>;
  isEnabled?: boolean;
  isVisible?: boolean;
};

type CommandRegistry = Record<string, CommandItem[]>;

type CommandBarContextValue = {
  commands: CommandItem[];
  focusRequestId: number;
  requestFocus: () => void;
  registerCommands: (sourceId: string, commands: CommandItem[]) => () => void;
  /** Whether the command palette overlay is open. */
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const CommandBarContext = createContext<CommandBarContextValue | undefined>(
  undefined,
);

/**
 * Context provider for the command bar. Manages the registry of commands and provides functions for registering/unregistering
 * commands and requesting focus. Also sets up a global keyboard shortcut (Cmd+K / Ctrl+K) for opening the command bar
 * from anywhere in the app, including when focus is currently in a text input.
 * @param param0
 * @returns
 */
export function CommandBarProvider({ children }: PropsWithChildren) {
  const [registry, setRegistry] = useState<CommandRegistry>({});
  const [focusRequestId, setFocusRequestId] = useState(0);

  /**
   * Increments the focus request ID to signal to the command bar that it should take focus. This is used both by the global
   * keyboard shortcut handler and can also be called by any component that wants to programmatically trigger the command bar
   * (e.g. a button in the UI).
   */
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const requestFocus = useCallback(() => {
    setFocusRequestId((prev) => prev + 1);
    setIsOpen(true);
  }, []);

  /**
   * Global key listener for opening the command palette with Cmd+K / Ctrl+K
   * from any focused element.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!((event.metaKey || event.ctrlKey) && event.code === "KeyK")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      requestFocus();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [requestFocus]);

  /**
   * Registers a set of commands under a given source ID. Returns an unregister function to remove those commands from
   * the registry. This allows components to dynamically add/remove commands as they mount/unmount or as their state changes.
   */
  const registerCommands = useCallback(
    (sourceId: string, commands: CommandItem[]) => {
      setRegistry((prev) => ({ ...prev, [sourceId]: commands }));

      return () => {
        setRegistry((prev) => {
          const next = { ...prev };
          delete next[sourceId];
          return next;
        });
      };
    },
    [],
  );

  /**
   * Flattens the command registry into a single array of commands, filtering out any that are not visible. This is the array
   * of commands that will be used by the command bar component to display results. The registry is kept as an object keyed
   * by source ID to make it easy to add/remove groups of commands without having to manage unique IDs for each command.
   */
  const commands = useMemo(() => {
    return Object.values(registry)
      .flat()
      .filter((cmd) => cmd.isVisible ?? true);
  }, [registry]);

  /**
   * Memoizes the context value to prevent unnecessary re-renders of consuming components. The commands array is included in the
   * dependencies, but since it's derived from the registry state, it will only change when the registry changes (i.e. when
   * commands are registered/unregistered).
   */
  const value = useMemo(
    () => ({
      commands,
      focusRequestId,
      requestFocus,
      registerCommands,
      isOpen,
      open,
      close,
    }),
    [commands, focusRequestId, requestFocus, registerCommands, isOpen, open, close],
  );

  return (
    <CommandBarContext.Provider value={value}>
      {children}
    </CommandBarContext.Provider>
  );
}

/**
 * Custom hook for consuming the CommandBarContext. Provides access to the list of registered commands, the current focus request ID,
 * and functions for requesting focus and registering commands. Throws an error if used outside of a CommandBarProvider.
 * @returns {CommandBarContextValue} The context value containing commands, focusRequestId, requestFocus, and registerCommands.
 */
export function useCommandBarContext(): CommandBarContextValue {
  const context = useContext(CommandBarContext);
  if (!context) {
    throw new Error(
      "useCommandBarContext must be used within CommandBarProvider",
    );
  }

  return context;
}

/**
 * Custom hook for registering commands from a component. Takes a source ID to group commands together, and an array of CommandItems.
 * Automatically handles unregistering those commands when the component unmounts or when the commands change.
 * @param {string} sourceId The unique identifier for the source of the commands.
 * @param {CommandItem[]} commands The array of CommandItems to register.
 */
export function useRegisterCommands(sourceId: string, commands: CommandItem[]) {
  const { registerCommands } = useCommandBarContext();

  useEffect(() => {
    return registerCommands(sourceId, commands);
  }, [commands, registerCommands, sourceId]);
}
